import Modal from "react-bootstrap/Modal";
import { AddIcon, RemoveIcon } from "../components/icons";
import { Wizard, Steps, Step } from "../components/Wizard";
import qttDisplay from "../utils/qttDisplay";
import toPriceDisplay from "../utils/toPriceDisplay";
import { isQuestionSatisfied } from "../utils/questions";
import QuestionsCardTotem from "./QuestionsCardTotem";

/**
 * Rodapé da tela de produto do totem, do tablet e do POS. Sem perguntas, a
 * quantidade fica aqui; com perguntas, o botão abre o wizard, uma pergunta por
 * tela, e a quantidade vai no último passo.
 *
 * `order` vem do useProductOrder.
 */
export default function FloatingProductOrderFooterTotem({ order }) {
  const { questions, addToCart, disabled, buttonLabel, cachedCost } = order;

  return (
    <footer className="totem-footer">
      {questions.length === 0 && <QttStepper order={order} />}
      <button
        type="button"
        className="btn btn-shop totem-btn totem-btn--primary"
        onClick={addToCart}
        disabled={disabled}
      >
        <span className="totem-btn__label">{buttonLabel}</span>
        {!disabled && <span>{toPriceDisplay(cachedCost)}</span>}
      </button>
      <QuestionsWizard order={order} />
    </footer>
  );
}

function QttStepper({ order }) {
  const { qtt, qttMinimum, isKilogram, increment, decrement } = order;

  return (
    <div className="totem-stepper">
      <button
        type="button"
        className="totem-stepper__button"
        onClick={decrement}
        disabled={qtt <= qttMinimum}
        aria-label="Diminuir quantidade"
      >
        <RemoveIcon />
      </button>
      <span className="totem-stepper__value" aria-live="polite">
        {qttDisplay(qtt, isKilogram)}
      </span>
      <button
        type="button"
        className="totem-stepper__button"
        onClick={increment}
        aria-label="Aumentar quantidade"
      >
        <AddIcon />
      </button>
    </div>
  );
}

function QuestionsWizard({ order }) {
  const { orderUuid, questions, answers, showWizard, closeWizard } = order;
  const totalSteps = questions.length + 1;

  return (
    <Modal
      backdrop="static"
      dialogClassName="totem-fullscreen-dialog"
      contentClassName="totem-fullscreen-dialog__content"
      show={showWizard}
      onHide={closeWizard}
    >
      <Wizard>
        <Steps>
          {questions.map((step, index) => (
            <Step
              key={step.id}
              id={`${step.id}`}
              render={({ next, previous }) => (
                <QuestionsCardTotem
                  question={step}
                  orderUuid={orderUuid}
                  isAvailableToOrder={true}
                  stepLabel={`Passo ${index + 1} de ${totalSteps}`}
                  actions={
                    <StepsButton
                      question={step}
                      currentStep={index}
                      next={next}
                      previous={previous}
                      cancel={closeWizard}
                      answers={answers}
                    />
                  }
                />
              )}
            />
          ))}
          <Step
            key="final-step"
            id="final-step"
            render={({ previous }) => (
              <StepQttAddCart
                order={order}
                currentStep={questions.length}
                stepLabel={`Passo ${totalSteps} de ${totalSteps}`}
                previous={previous}
              />
            )}
          />
        </Steps>
      </Wizard>
    </Modal>
  );
}

function StepsButton({
  currentStep,
  question,
  next,
  previous,
  cancel,
  answers,
}) {
  const disabled = !isQuestionSatisfied(question, answers);
  const first = currentStep === 0;

  return (
    <>
      <button
        type="button"
        className="totem-btn totem-btn--secondary"
        onClick={first ? cancel : previous}
      >
        Voltar
      </button>
      <button
        type="button"
        className="btn btn-shop totem-btn totem-btn--primary"
        onClick={next}
        disabled={disabled}
      >
        Continuar
      </button>
    </>
  );
}

function StepQttAddCart({ order, currentStep, stepLabel, previous }) {
  const { product, answers, addToCart, closeWizard, cachedCost } = order;
  const first = currentStep === 0;
  const choices = answers.map((a) => {
    const optionLabel = a.question.options.find(
      (o) => o.id === a.optionId,
    ).label;
    return `${a.qtt}x ${optionLabel}`;
  });

  return (
    <div className="totem-question">
      <div className="totem-question__scroll">
        <header className="totem-question__header">
          <p className="totem-question__step">{stepLabel}</p>
          <h2 className="totem-question__title">Tudo certo?</h2>
        </header>
        <div className="totem-summary">
          {product.pictureUrl && (
            <img
              src={product.pictureUrl}
              alt=""
              className="totem-summary__img"
            />
          )}
          <p className="totem-summary__name">{product.name}</p>
          {choices.length > 0 && (
            <ul className="totem-summary__choices">
              {choices.map((choice, index) => (
                <li key={index} className="totem-chip">
                  {choice}
                </li>
              ))}
            </ul>
          )}
          <p className="totem-summary__label">Quantidade</p>
          <QttStepper order={order} />
        </div>
      </div>
      <footer className="totem-question__footer">
        <button
          type="button"
          className="totem-btn totem-btn--secondary"
          onClick={first ? closeWizard : previous}
        >
          Voltar
        </button>
        <button
          type="button"
          className="btn btn-shop totem-btn totem-btn--primary"
          onClick={addToCart}
        >
          <span className="totem-btn__label">Adicionar</span>
          <span>{toPriceDisplay(cachedCost)}</span>
        </button>
      </footer>
    </div>
  );
}
